import { useDeleteProductPriceMutation, useGetProductPricesQuery } from '@codezo/graphql';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { SectionCard } from '../../components/ui/section-card';
import { useCurrencyStore } from '../../stores/currency';
import { HOUSE_LADDER } from './product-price-tiers';
import { VetterSelector } from '../../components/selectors/VetterSelector';
import { groupPriceLists, PriceListBlock, type PriceRow } from './PriceListBlock';
import { SetProductPricesModal, type PricesEditorMode } from './SetProductPricesModal';

/** The supplier filter is three-valued, and each value asks a different question. */
const ALL = 'all';
const HOUSE = 'house';

/**
 * The catalogue cost ladders for one product: the default prices, and one list
 * per supplier.
 *
 * Costs, never sell prices — this is the vetter cost basis and the backend keeps
 * it admin-only. Shown in the header currency, converted by the backend, like
 * every other admin figure.
 */
export function ProductPricesSection({ productId }: { productId: string }) {
	const [supplier, setSupplier] = useState<string>(ALL);
	const [includeInactive, setIncludeInactive] = useState(false);
	const [editing, setEditing] = useState<PricesEditorMode | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<PriceRow | null>(null);

	/**
	 * Omitted returns every row; a supplier id returns that supplier's rows AND
	 * the default rows it falls back to; an explicit null returns the default
	 * alone. The middle answer is the useful one — a supplier's list without the
	 * defaults hides what it would actually be quoted at on its unpriced tiers.
	 */
	const vetterEntityId = supplier === ALL ? undefined : supplier === HOUSE ? null : supplier;

	const { selectedCurrency } = useCurrencyStore();
	const { data, loading, refetch } = useGetProductPricesQuery({
		variables: {
			input: {
				productId,
				...(supplier === ALL ? {} : { vetterEntityId }),
				includeInactive,
			},
			currency: selectedCurrency,
		},
		fetchPolicy: 'no-cache',
	});

	const rows = useMemo(() => data?.getProductPrices ?? [], [data]);
	const lists = useMemo(() => groupPriceLists(rows), [rows]);

	/* Said once for the card rather than on every row. One stored currency
	   names it; several point to the per-cost tooltip. */
	const storedIn = [...new Set(rows.map((r) => r.currency))];
	const currencyNote = !storedIn.some((c) => c !== selectedCurrency)
		? null
		: storedIn.length === 1
			? `Shown in ${selectedCurrency} · stored in ${storedIn[0]}`
			: `Shown in ${selectedCurrency} · hover a cost to see what is stored`;

	const [deletePrice, { loading: deleting }] = useDeleteProductPriceMutation();

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deletePrice({ variables: { input: { id: deleteTarget.id } } });
			toast.success('Price removed');
			setDeleteTarget(null);
			void refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove price');
		}
	};

	return (
		<>
			<SectionCard
				title="Catalogue prices"
				action={
					/* A price is added or changed one at a time; the modal still
					   submits whole ladders underneath, because that is what the
					   mutation replaces, but that is not the admin's problem. */
					<button
						onClick={() =>
							setEditing({
								kind: 'new',
								// Start on whatever the list is filtered to.
								vetterEntityId:
									supplier === ALL
										? undefined
										: supplier === HOUSE
											? HOUSE_LADDER
											: supplier,
							})
						}
						title="Add a price to this product"
					>
						<Plus size={12} />
						Add price
					</button>
				}
			>
				<div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-1 px-4 py-3">
					<span className="text-xs text-text-3">{currencyNote}</span>
					<div className="flex flex-wrap items-center gap-4">
						<label className="flex cursor-pointer items-center gap-2">
							<Checkbox
								checked={includeInactive}
								onCheckedChange={(v) => setIncludeInactive(v === true)}
							/>
							<span className="text-sm text-text-3">Show inactive</span>
						</label>
						{/* `VetterSelector` fetches only once opened. This section renders
						    on the product Overview, so an eager 200-row supplier query
						    would run on every product page view. */}
						<VetterSelector
							value={supplier}
							onChange={(v) => setSupplier(v || ALL)}
							extraOptions={[
								{ value: ALL, label: 'All suppliers' },
								{ value: HOUSE, label: 'Default price only' },
							]}
							placeholder="All suppliers"
							selectedLabel={
								supplier === ALL
									? 'All suppliers'
									: supplier === HOUSE
										? 'Default price only'
										: undefined
							}
							className="w-52"
						/>
					</div>
				</div>

				{loading ? (
					<div className="space-y-2 p-4">
						{[0, 1, 2].map((i) => (
							<div key={i} className="h-8 animate-pulse rounded bg-surface-fill" />
						))}
					</div>
				) : lists.length === 0 ? (
					<div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
						<div className="text-sm text-text-3">
							No catalogue prices for this product yet.
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setEditing({ kind: 'new' })}
						>
							<Plus size={14} data-icon="inline-start" />
							Add price
						</Button>
					</div>
				) : (
					lists.map((list) => (
						<PriceListBlock
							key={list.key}
							list={list}
							onAdd={() =>
								setEditing({
									kind: 'new',
									vetterEntityId: list.vetterEntityId ?? HOUSE_LADDER,
								})
							}
							onEdit={(row) => setEditing({ kind: 'tier', tierId: row.id })}
							onDelete={setDeleteTarget}
						/>
					))
				)}
			</SectionCard>

			{editing && (
				<SetProductPricesModal
					open
					onOpenChange={(o) => !o && setEditing(null)}
					productId={productId}
					mode={editing}
					onSuccess={() => void refetch()}
				/>
			)}

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
				title="Remove this price?"
				description={
					deleteTarget
						? `The ${deleteTarget.quantity.toLocaleString()} pcs price for ${
								deleteTarget.vetterEntity?.name ?? 'the default price list'
							} will be deleted.`
						: ''
				}
				confirmLabel="Remove"
				loading={deleting}
				onConfirm={handleDelete}
			/>
		</>
	);
}
